import { request } from './request'
import { useEnvApi } from '../composables/useEnvApi'
import { useServerFetch } from '../composables/useServerFetch'
import { normalizeSoftwareRankingData, normalizeSoftwareToolCategories } from '../utils/software'
import type {
  SoftwareDeviceType,
  SoftwareHomeDeviceData,
  SoftwareRankingData,
  SoftwareToolCategory,
} from '../types/software'

export type {
  SoftwareDeviceType,
  SoftwareHomeDeviceData,
  SoftwareItem,
  SoftwarePlatformData,
  SoftwareRankingData,
  SoftwareToolCategory,
} from '../types/software'

const SOFTWARE_HOME_API_PATHS = {
  curatedForexTools: '/api/v1/softwate/get_curated_forex_tools_app',
  downloadRanking: '/api/v2/softwate/get_software_download_ranking_list',
  monthlyActiveRanking: '/api/v1/softwate/get_month_active',
  popularSoftware: '/api/v1/softwate/get_popular_software',
} as const

/** SSR 使用 useServerFetch，客户端复用 request；两端共享同一域名与参数。 */
const requestSoftwareHomeData = (url: string, deviceType: SoftwareDeviceType): Promise<unknown> => {
  const api = useEnvApi()

  if (import.meta.server) {
    return useServerFetch({
      baseURL: api.wikiStore,
      url,
      method: 'GET',
      params: { deviceType },
    })
  }

  return request<unknown, unknown>({
    baseURL: api.v4,
    url,
    method: 'GET',
    params: { deviceType },
  })
}

/** 获取指定终端的精选外汇工具。 */
export const getCuratedForexTools = async (deviceType: SoftwareDeviceType): Promise<SoftwareToolCategory[]> => {
  const response = await requestSoftwareHomeData(SOFTWARE_HOME_API_PATHS.curatedForexTools, deviceType)
  return normalizeSoftwareToolCategories(response)
}

/** 获取指定终端的月度下载排行。 */
export const getSoftwareDownloadRanking = async (deviceType: SoftwareDeviceType): Promise<SoftwareRankingData> => {
  const response = await requestSoftwareHomeData(SOFTWARE_HOME_API_PATHS.downloadRanking, deviceType)
  return normalizeSoftwareRankingData(response)
}

/** 获取指定终端的月度活跃排行。 */
export const getSoftwareMonthlyActiveRanking = async (deviceType: SoftwareDeviceType): Promise<SoftwareRankingData> => {
  const response = await requestSoftwareHomeData(SOFTWARE_HOME_API_PATHS.monthlyActiveRanking, deviceType)
  return normalizeSoftwareRankingData(response)
}

/** 获取指定终端的热门软件。 */
export const getPopularSoftware = async (deviceType: SoftwareDeviceType): Promise<SoftwareRankingData> => {
  const response = await requestSoftwareHomeData(SOFTWARE_HOME_API_PATHS.popularSoftware, deviceType)
  return normalizeSoftwareRankingData(response)
}

/** 每个失败请求获得独立空列表，避免不同模块共享可变数组。 */
const emptyRanking = (): SoftwareRankingData => ({ global: [], current: [] })

export const createEmptySoftwareHomeDeviceData = (deviceType: SoftwareDeviceType): SoftwareHomeDeviceData => ({
  deviceType,
  curated: [],
  download: emptyRanking(),
  active: emptyRanking(),
  popular: emptyRanking(),
  errors: [],
})

/** 请求单个终端的四组首页数据，供页面同时收集 IOS 与 Android 结果。 */
export const getSoftwareHomeDataForDevice = async (deviceType: SoftwareDeviceType): Promise<SoftwareHomeDeviceData> => {
  const results = await Promise.allSettled([
    getCuratedForexTools(deviceType),
    getSoftwareDownloadRanking(deviceType),
    getSoftwareMonthlyActiveRanking(deviceType),
    getPopularSoftware(deviceType),
  ])
  const errors: string[] = []
  const takeResult = <T>(result: PromiseSettledResult<T>, key: string, fallback: T): T => {
    if (result.status === 'fulfilled') return result.value
    errors.push(key)
    return fallback
  }
  return {
    deviceType,
    curated: takeResult(results[0], 'curated', []),
    download: takeResult(results[1], 'download', emptyRanking()),
    active: takeResult(results[2], 'active', emptyRanking()),
    popular: takeResult(results[3], 'popular', emptyRanking()),
    errors,
  }
}
